import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import {
  deleteConversation as apiDeleteConversation,
  renameConversation as apiRenameConversation,
  getConversation,
  listConversations,
  sendMessage,
  sendMessageStream,
} from "@/api/ai/aiApi";
import { NEW_STUDIO_SYSTEM_PROMPT } from "@/api/ai/newStudioSystemPrompt";
import {
  type ContextItem,
  type Conversation,
  type ConversationContext,
  type ConversationMode,
  type ConversationSummary,
  type DisplayMessage,
  MessageStatus,
  MessageType,
  type ToolCallEntry,
} from "@/api/ai/types";
import { useToast } from "@/components/toast/ToastProvider";
import { userStorage } from "@/utils/userStorage";

// Errors reach the user as toasts. The engine owns chat data (list, history,
// titles): a change is applied optimistically, and a refused request puts
// the list back and says so, rather than keeping a local copy the server
// never saw.
type ReportError = (message: string) => void;

/** A conversation id change, e.g. a local temp id replaced by a backend id. */
interface IdRemap {
  from: string;
  to: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CONTEXT_BLOCK_RE = /^<context>[\s\S]*?<\/context>\s*/;

function stripContextBlock(text: string): string {
  return text.replace(CONTEXT_BLOCK_RE, "");
}

function toDisplayMessage(m: {
  role: string;
  content: string;
  timestamp: string;
}): DisplayMessage {
  const isUser = m.role === "user";
  return {
    messageId: uuidv4(),
    message: isUser ? stripContextBlock(m.content) : m.content,
    type: isUser ? MessageType.User : MessageType.Assistant,
    timestamp: new Date(m.timestamp).getTime(),
    status: MessageStatus.Complete,
  };
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface AiChatState {
  messages: DisplayMessage[];
  currentConversationId: string | null;
  conversations: ConversationSummary[];
  inputValue: string;
  /** A message a host asked to send on its behalf (the editors' "Fix
   *  with AI"); the sidebar submits it once it is mounted and has it in
   *  the input, then clears it. */
  queuedMessage: string | null;
  isLoading: boolean;
  /** The current chat's messages are known: a fresh chat's empty list,
   *  or a saved chat's fetched history. False while that fetch is still
   *  on its way (an unloaded chat is not an empty one). */
  messagesLoaded: boolean;
  isLoadingConversations: boolean;
  showAiSidebar: boolean;
  toolActivityVersion: number;
}

interface StreamMessageOptions {
  allowWrites?: boolean;
  mode?: ConversationMode;
}

interface AiChatActions {
  setInputValue: (value: string) => void;
  queueMessage: (text: string) => void;
  clearQueuedMessage: () => void;
  sendUserMessage: (context: ContextItem[]) => Promise<void>;
  sendUserMessageStream: (
    context: ContextItem[],
    systemPrompt?: string,
    options?: StreamMessageOptions,
  ) => void;
  newConversation: () => void;
  selectConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  renameConversation: (id: string, title: string) => void;
  stopGeneration: () => void;
  setShowAiSidebar: (show: boolean) => void;
}

type AiChatContextValue = AiChatState & AiChatActions;

const AiChatContext = createContext<AiChatContextValue | null>(null);

// ---------------------------------------------------------------------------
// Conversations hook
// ---------------------------------------------------------------------------

/** Put a summary back into the list as it was: in place if still listed,
 *  else at `index`. The undo for an optimistic rename or delete. */
function withSummaryRestored(
  list: ConversationSummary[],
  summary: ConversationSummary,
  index: number,
): ConversationSummary[] {
  if (list.some((c) => c.id === summary.id)) {
    return list.map((c) => (c.id === summary.id ? summary : c));
  }
  return [...list.slice(0, index), summary, ...list.slice(index)];
}

/** The list and the selection: what is listed, which chat is open, and the
 *  local-only edits to that list. The requests that go to the engine live
 *  in `useConversationMutations`. */
function useConversations(
  context: ConversationContext | undefined,
  reportError: ReportError,
) {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<
    string | null
  >(null);
  // True from the first render: the list is fetched on mount, and a first
  // paint that claimed "no chats" would flash before the real list lands.
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const initialLoadDoneRef = useRef(false);

  const loadInitialConversations = useCallback(async () => {
    if (initialLoadDoneRef.current) return null;
    initialLoadDoneRef.current = true;

    setIsLoadingConversations(true);
    try {
      const loaded = await listConversations(undefined, context);
      setConversations(loaded);
      if (loaded.length > 0) {
        setCurrentConversationId(loaded[0].id);
        return loaded[0].id;
      }
    } catch (error) {
      console.error("Failed to load conversations:", error);
      reportError("Could not load your chats.");
    } finally {
      setIsLoadingConversations(false);
    }
    return null;
  }, [context, reportError]);

  const addConversation = useCallback(
    (id: string, title: string) => {
      const now = new Date().toISOString();
      setConversations((prev) => [
        { id, title, context, created_at: now, updated_at: now },
        ...prev,
      ]);
      setCurrentConversationId(id);
    },
    [context],
  );

  const updateTitle = useCallback((conversationId: string, title: string) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === conversationId
          ? {
              ...c,
              title: title.slice(0, 50),
              updated_at: new Date().toISOString(),
            }
          : c,
      ),
    );
  }, []);

  const removeLocalConversation = useCallback((id: string) => {
    setConversations((prev) => prev.filter((c) => c.id !== id));
  }, []);

  return {
    conversations,
    setConversations,
    currentConversationId,
    isLoadingConversations,
    setCurrentConversationId,
    loadInitialConversations,
    addConversation,
    updateTitle,
    removeLocalConversation,
  };
}

// ---------------------------------------------------------------------------
// Conversation mutations hook
// ---------------------------------------------------------------------------

interface ConversationMutationsOptions {
  conversations: ConversationSummary[];
  currentConversationId: string | null;
  setConversations: Dispatch<SetStateAction<ConversationSummary[]>>;
  setCurrentConversationId: Dispatch<SetStateAction<string | null>>;
  updateTitle: (id: string, title: string) => void;
  removeLocalConversation: (id: string) => void;
  reportError: ReportError;
}

/** The edits the engine owns: each is applied to the local list first, and a
 *  refused request puts the list back and says so. */
function useConversationMutations({
  conversations,
  currentConversationId,
  setConversations,
  setCurrentConversationId,
  updateTitle,
  removeLocalConversation,
  reportError,
}: ConversationMutationsOptions) {
  const restoreConversation = useCallback(
    (summary: ConversationSummary, index: number) => {
      setConversations((prev) => withSummaryRestored(prev, summary, index));
    },
    [setConversations],
  );

  // The latest rename per chat. A rename refused after a newer one was
  // sent must not roll the title back over the newer name.
  const renameTokens = useRef<Map<string, number>>(new Map());

  /** Rename, optimistically. `fallbackTitle` is the rollback when the chat
   *  is not in `conversations` yet (a draft promoted and renamed in the
   *  same tick). */
  const renameConversation = useCallback(
    (id: string, title: string, fallbackTitle?: string) => {
      const index = conversations.findIndex((c) => c.id === id);
      const previous = conversations[index];
      const token = (renameTokens.current.get(id) ?? 0) + 1;
      renameTokens.current.set(id, token);
      updateTitle(id, title);
      apiRenameConversation({ id, title }).catch((error) => {
        console.error("Failed to rename conversation:", error);
        if (renameTokens.current.get(id) !== token) return;
        if (previous) restoreConversation(previous, index);
        else if (fallbackTitle !== undefined) updateTitle(id, fallbackTitle);
        reportError("Could not rename the chat.");
      });
    },
    [conversations, updateTitle, restoreConversation, reportError],
  );

  const removeConversation = useCallback(
    async (id: string) => {
      const index = conversations.findIndex((c) => c.id === id);
      const removed = conversations[index];
      removeLocalConversation(id);

      if (currentConversationId === id) {
        const remaining = conversations.filter((c) => c.id !== id);
        setCurrentConversationId(remaining.length > 0 ? remaining[0].id : null);
      }

      try {
        await apiDeleteConversation(id);
        return true;
      } catch (error) {
        console.error("Failed to delete conversation from backend:", error);
        if (removed) restoreConversation(removed, index);
        reportError("Could not delete the chat.");
        return false;
      }
    },
    [
      currentConversationId,
      conversations,
      setCurrentConversationId,
      removeLocalConversation,
      restoreConversation,
      reportError,
    ],
  );

  return { renameConversation, removeConversation };
}

// ---------------------------------------------------------------------------
// Messages hook
// ---------------------------------------------------------------------------

type MessageMap = Record<string, DisplayMessage[]>;

/** Return a copy of `prev` where `id` is present iff `present` is true. */
function withSetMember(
  prev: Set<string>,
  id: string,
  present: boolean,
): Set<string> {
  if (prev.has(id) === present) return prev;
  const next = new Set(prev);
  if (present) {
    next.add(id);
  } else {
    next.delete(id);
  }
  return next;
}

function moveSetMember(prev: Set<string>, remap: IdRemap): Set<string> {
  if (!prev.has(remap.from)) return prev;
  return withSetMember(withSetMember(prev, remap.from, false), remap.to, true);
}

function withConversation(
  map: MessageMap,
  id: string,
  msgs: DisplayMessage[],
): MessageMap {
  return { ...map, [id]: msgs };
}

function withoutConversation(map: MessageMap, id: string): MessageMap {
  const { [id]: _, ...rest } = map;
  return rest;
}

/** Mark a pending/streaming message as finished, keeping any partial text. */
function finalizeMessage(m: DisplayMessage): DisplayMessage {
  const inFlight =
    m.status === MessageStatus.Streaming || m.status === MessageStatus.Pending;
  if (!inFlight) return m;
  return {
    ...m,
    message: m.message || "Generation stopped",
    status: m.message ? MessageStatus.Complete : MessageStatus.Error,
  };
}

function toChatDisplayMessages(conversation: Conversation): DisplayMessage[] {
  return conversation.messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map(toDisplayMessage);
}

function messagesOf(map: MessageMap, id: string | null): DisplayMessage[] {
  return (id && map[id]) || [];
}

function withMessageUpdated(
  map: MessageMap,
  id: string,
  messageId: string,
  updater: (m: DisplayMessage) => DisplayMessage,
): MessageMap {
  const updated = messagesOf(map, id).map((m) =>
    m.messageId === messageId ? updater(m) : m,
  );
  return withConversation(map, id, updated);
}

function withConversationRemapped(map: MessageMap, remap: IdRemap): MessageMap {
  return withConversation(
    withoutConversation(map, remap.from),
    remap.to,
    messagesOf(map, remap.from),
  );
}

function useMessages(
  currentConversationId: string | null,
  reportError: ReportError,
) {
  const [messagesByConversation, setMessagesByConversation] =
    useState<MessageMap>({});
  const [loadingConversationIds, setLoadingConversationIds] = useState<
    Set<string>
  >(new Set());
  const loadedConversationsRef = useRef<Set<string>>(new Set());

  const messages = messagesOf(messagesByConversation, currentConversationId);
  const messagesLoaded =
    currentConversationId != null &&
    currentConversationId in messagesByConversation;

  // Loading is tracked per conversation so an in-flight generation in one
  // chat never blocks interacting with another.
  const isLoading =
    currentConversationId != null &&
    loadingConversationIds.has(currentConversationId);

  const setConversationLoading = useCallback((id: string, loading: boolean) => {
    setLoadingConversationIds((prev) => withSetMember(prev, id, loading));
  }, []);

  /** Mark any pending/streaming messages in a conversation as finished. */
  const finalizePendingMessages = useCallback((id: string) => {
    setMessagesByConversation((prev) =>
      withConversation(prev, id, messagesOf(prev, id).map(finalizeMessage)),
    );
  }, []);

  const loadMessages = useCallback(
    async (id: string) => {
      if (loadedConversationsRef.current.has(id)) return;
      loadedConversationsRef.current.add(id);

      try {
        const mapped = toChatDisplayMessages(await getConversation(id));
        setMessagesByConversation((prev) => withConversation(prev, id, mapped));
      } catch (error) {
        console.error("Failed to load conversation messages:", error);
        loadedConversationsRef.current.delete(id);
        reportError("Could not load the chat history.");
      }
    },
    [reportError],
  );

  const initializeConversation = useCallback((id: string) => {
    setMessagesByConversation((prev) => withConversation(prev, id, []));
  }, []);

  const clearConversationMessages = useCallback((id: string) => {
    setMessagesByConversation((prev) => withoutConversation(prev, id));
    loadedConversationsRef.current.delete(id);
  }, []);

  const remapConversation = useCallback((remap: IdRemap) => {
    setMessagesByConversation((prev) => withConversationRemapped(prev, remap));
    setLoadingConversationIds((prev) => moveSetMember(prev, remap));
    loadedConversationsRef.current.delete(remap.from);
    loadedConversationsRef.current.add(remap.to);
  }, []);

  const appendMessage = useCallback((id: string, msg: DisplayMessage) => {
    setMessagesByConversation((prev) =>
      withConversation(prev, id, [...messagesOf(prev, id), msg]),
    );
  }, []);

  const updateMessage = useCallback(
    (
      id: string,
      messageId: string,
      updater: (m: DisplayMessage) => DisplayMessage,
    ) => {
      setMessagesByConversation((prev) =>
        withMessageUpdated(prev, id, messageId, updater),
      );
    },
    [],
  );

  return {
    messages,
    messagesLoaded,
    messagesByConversation,
    isLoading,
    setConversationLoading,
    finalizePendingMessages,
    loadMessages,
    initializeConversation,
    clearConversationMessages,
    remapConversation,
    appendMessage,
    updateMessage,
  };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ResolvedConversation {
  conversationId: string;
  isFirstMessage: boolean;
}

interface ResolveConversationOpts {
  currentConversationId: string | null;
  messagesByConversation: Record<string, DisplayMessage[]>;
  text: string;
  addConversation: (id: string, title: string) => void;
  initializeConversation: (id: string) => void;
  updateTitle: (id: string, title: string) => void;
  /** The current draft was named by the user; keep that over the
   *  first-message snippet. */
  keepTitle: boolean;
}

function resolveConversation(
  opts: ResolveConversationOpts,
): ResolvedConversation {
  const {
    currentConversationId,
    messagesByConversation,
    text,
    addConversation,
    initializeConversation,
    updateTitle,
    keepTitle,
  } = opts;
  if (!currentConversationId) {
    const conversationId = uuidv4();
    addConversation(conversationId, text.slice(0, 50));
    initializeConversation(conversationId);
    return { conversationId, isFirstMessage: true };
  }

  const existing = messagesByConversation[currentConversationId] || [];
  if (existing.length === 0) {
    if (!keepTitle) updateTitle(currentConversationId, text);
    return { conversationId: currentConversationId, isFirstMessage: true };
  }

  return { conversationId: currentConversationId, isFirstMessage: false };
}

function createUserMessage(
  text: string,
  context: ContextItem[],
): DisplayMessage {
  return {
    messageId: uuidv4(),
    message: text,
    type: MessageType.User,
    timestamp: Date.now(),
    status: MessageStatus.Complete,
    context,
  };
}

function createAssistantPlaceholder(status: MessageStatus): DisplayMessage {
  return {
    messageId: uuidv4(),
    message: "",
    type: MessageType.Assistant,
    timestamp: Date.now(),
    status,
  };
}

// Expanded/collapsed state survives a page refresh (per user).
const AI_SIDEBAR_KEY = "ai-sidebar-open";

function loadAiSidebarOpen(): boolean {
  return userStorage.getItem(AI_SIDEBAR_KEY) === "true";
}

interface AiChatProviderProps {
  children: ReactNode;
  conversationContext?: ConversationContext;
}

export function AiChatProvider({
  children,
  conversationContext,
}: AiChatProviderProps) {
  const [inputValue, setInputValue] = useState("");
  const [queuedMessage, setQueuedMessage] = useState<string | null>(null);
  // The queued text lands in the input as well, so the sidebar's submit
  // path (which reads the input) sends exactly it.
  const queueMessage = useCallback((text: string) => {
    setInputValue(text);
    setQueuedMessage(text);
  }, []);
  const clearQueuedMessage = useCallback(() => setQueuedMessage(null), []);
  const [showAiSidebar, setShowAiSidebar] = useState(loadAiSidebarOpen);

  useEffect(() => {
    userStorage.setItem(AI_SIDEBAR_KEY, String(showAiSidebar));
  }, [showAiSidebar]);
  const [toolActivityVersion, setToolActivityVersion] = useState(0);
  // One abort handle per conversation — streams run independently.
  const abortStreamsRef = useRef<Map<string, () => void>>(new Map());
  // Chats started with New that have no message yet. They exist only in
  // this list (the backend creates a chat on its first message), so one
  // the user walks away from is dropped rather than left behind as an
  // empty "New conversation".
  const draftIdsRef = useRef<Set<string>>(new Set());
  // A draft renamed before its first message: the engine has nothing to
  // rename yet, so the name waits here and is sent once the chat exists.
  const draftTitlesRef = useRef<Map<string, string>>(new Map());

  const { toast } = useToast();
  const reportError = useCallback<ReportError>(
    (message) => {
      toast({ state: "Error", message });
    },
    [toast],
  );

  const {
    conversations,
    setConversations,
    currentConversationId,
    isLoadingConversations,
    setCurrentConversationId,
    loadInitialConversations,
    addConversation,
    updateTitle,
    removeLocalConversation,
  } = useConversations(conversationContext, reportError);

  const { renameConversation: renameSavedConversation, removeConversation } =
    useConversationMutations({
      conversations,
      currentConversationId,
      setConversations,
      setCurrentConversationId,
      updateTitle,
      removeLocalConversation,
      reportError,
    });

  const {
    messages,
    messagesLoaded,
    messagesByConversation,
    isLoading,
    setConversationLoading,
    finalizePendingMessages,
    loadMessages,
    initializeConversation,
    clearConversationMessages,
    remapConversation,
    appendMessage,
    updateMessage,
  } = useMessages(currentConversationId, reportError);

  // Load conversations on mount
  useEffect(() => {
    const init = async () => {
      const initialId = await loadInitialConversations();
      if (initialId) {
        await loadMessages(initialId);
      }
    };
    init();
  }, [loadInitialConversations, loadMessages]);

  /** Leaving the current chat for `nextId`: an unsent draft is dropped. */
  /** Remove every unsent draft from the list. */
  const dropAllDrafts = useCallback(() => {
    for (const id of draftIdsRef.current) {
      draftTitlesRef.current.delete(id);
      clearConversationMessages(id);
      removeLocalConversation(id);
    }
    draftIdsRef.current.clear();
  }, [clearConversationMessages, removeLocalConversation]);

  const dropDraftOnLeave = useCallback(
    (nextId: string) => {
      const id = currentConversationId;
      if (id == null || id === nextId || !draftIdsRef.current.has(id)) return;
      draftIdsRef.current.delete(id);
      draftTitlesRef.current.delete(id);
      clearConversationMessages(id);
      removeLocalConversation(id);
    },
    [currentConversationId, clearConversationMessages, removeLocalConversation],
  );

  const newConversation = useCallback(() => {
    const id = uuidv4();
    dropDraftOnLeave(id);
    draftIdsRef.current.add(id);
    addConversation(id, "New conversation");
    initializeConversation(id);
  }, [dropDraftOnLeave, addConversation, initializeConversation]);

  const selectConversation = useCallback(
    async (id: string) => {
      dropDraftOnLeave(id);
      setCurrentConversationId(id);
      await loadMessages(id);
    },
    [dropDraftOnLeave, setCurrentConversationId, loadMessages],
  );

  const deleteConversation = useCallback(
    async (id: string) => {
      const abort = abortStreamsRef.current.get(id);
      if (abort) {
        abort();
        abortStreamsRef.current.delete(id);
      }
      setConversationLoading(id, false);
      draftIdsRef.current.delete(id);
      draftTitlesRef.current.delete(id);
      clearConversationMessages(id);
      const wasCurrent = currentConversationId === id;
      const deleted = await removeConversation(id);
      // A refused delete of the open chat reopens it, history refetched,
      // in place of whatever the removal had moved to — including a draft
      // the page started meanwhile (the Agent lands on New), which the
      // closure here predates, so every unsent draft goes explicitly.
      if (!deleted && wasCurrent) {
        dropAllDrafts();
        setCurrentConversationId(id);
        await loadMessages(id);
      }
    },
    [
      currentConversationId,
      setConversationLoading,
      clearConversationMessages,
      removeConversation,
      dropAllDrafts,
      setCurrentConversationId,
      loadMessages,
    ],
  );

  const renameConversation = useCallback(
    (id: string, title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return;
      if (draftIdsRef.current.has(id)) {
        draftTitlesRef.current.set(id, trimmed);
        updateTitle(id, trimmed);
        return;
      }
      renameSavedConversation(id, trimmed);
    },
    [updateTitle, renameSavedConversation],
  );

  /** The draft became a real chat under `newId`: list it under the name
   *  the user chose (sent to the engine now) or the first-message snippet. */
  const promoteDraft = useCallback(
    (remap: IdRemap, text: string) => {
      const chosen = draftTitlesRef.current.get(remap.from);
      draftTitlesRef.current.delete(remap.from);
      removeLocalConversation(remap.from);
      const fromMessage = text.slice(0, 50);
      addConversation(remap.to, chosen ?? fromMessage);
      remapConversation(remap);
      // The new summary is not in the rename's list yet, so the
      // first-message title is handed over as the rollback.
      if (chosen) renameSavedConversation(remap.to, chosen, fromMessage);
    },
    [
      removeLocalConversation,
      addConversation,
      remapConversation,
      renameSavedConversation,
    ],
  );

  const stopGeneration = useCallback(() => {
    const id = currentConversationId;
    if (!id) return;
    const abort = abortStreamsRef.current.get(id);
    if (abort) {
      abort();
      abortStreamsRef.current.delete(id);
    }
    setConversationLoading(id, false);
    finalizePendingMessages(id);
  }, [currentConversationId, setConversationLoading, finalizePendingMessages]);

  const sendUserMessage = useCallback(
    async (context: ContextItem[]) => {
      const text = inputValue.trim();
      if (!text) return;

      const resolved = resolveConversation({
        currentConversationId,
        messagesByConversation,
        text,
        addConversation,
        initializeConversation,
        updateTitle,
        keepTitle: draftTitlesRef.current.has(currentConversationId ?? ""),
      });
      let conversationId = resolved.conversationId;
      // Its first request makes the draft a real chat.
      draftIdsRef.current.delete(conversationId);

      setInputValue("");

      const userMsg = createUserMessage(text, context);
      appendMessage(conversationId, userMsg);

      const assistantMsg = createAssistantPlaceholder(MessageStatus.Pending);
      appendMessage(conversationId, assistantMsg);

      setConversationLoading(conversationId, true);

      try {
        const backendConvId = resolved.isFirstMessage
          ? undefined
          : conversationId;

        const response = await sendMessage({
          message: text,
          context,
          conversationId: backendConvId,
          allowWrites: false,
          conversationContext,
          systemPrompt: NEW_STUDIO_SYSTEM_PROMPT,
        });

        const assistantUpdate: DisplayMessage = {
          messageId: assistantMsg.messageId,
          message: response.response,
          type: MessageType.Assistant,
          timestamp: Date.now(),
          status: MessageStatus.Complete,
          toolRounds: response.tool_rounds,
        };

        if (response.conversation_id !== conversationId) {
          const newId = response.conversation_id;
          promoteDraft({ from: conversationId, to: newId }, text);
          updateMessage(newId, assistantMsg.messageId, (m) => ({
            ...m,
            ...assistantUpdate,
          }));
          conversationId = newId;
        } else {
          updateMessage(conversationId, assistantMsg.messageId, (m) => ({
            ...m,
            ...assistantUpdate,
          }));
        }

        if (response.tool_rounds > 0) {
          setToolActivityVersion((v) => v + 1);
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        updateMessage(conversationId, assistantMsg.messageId, (m) => ({
          ...m,
          message: `Error: ${errorMsg}`,
          status: MessageStatus.Error,
        }));
      } finally {
        // conversationId may have been remapped to the backend id mid-flight;
        // remapConversation moves the loading flag, so clear the final id.
        setConversationLoading(conversationId, false);
      }
    },
    [
      inputValue,
      currentConversationId,
      messagesByConversation,
      addConversation,
      initializeConversation,
      updateTitle,
      promoteDraft,
      appendMessage,
      updateMessage,
      setConversationLoading,
      conversationContext,
    ],
  );

  const sendUserMessageStream = useCallback(
    (
      context: ContextItem[],
      systemPrompt?: string,
      options?: StreamMessageOptions,
    ) => {
      const text = inputValue.trim();
      if (!text) return;

      const resolved = resolveConversation({
        currentConversationId,
        messagesByConversation,
        text,
        addConversation,
        initializeConversation,
        updateTitle,
        keepTitle: draftTitlesRef.current.has(currentConversationId ?? ""),
      });
      // Its first request makes the draft a real chat.
      draftIdsRef.current.delete(resolved.conversationId);

      setInputValue("");

      const userMsg = createUserMessage(text, context);
      appendMessage(resolved.conversationId, userMsg);

      const assistantMsg = createAssistantPlaceholder(MessageStatus.Streaming);
      appendMessage(resolved.conversationId, assistantMsg);

      // Replace any still-running stream for this conversation only —
      // streams in other conversations keep going.
      const existingAbort = abortStreamsRef.current.get(
        resolved.conversationId,
      );
      if (existingAbort) {
        existingAbort();
        abortStreamsRef.current.delete(resolved.conversationId);
      }

      setConversationLoading(resolved.conversationId, true);

      let activeConversationId = resolved.conversationId;
      let accumulatedText = "";
      let toolRounds = 0;
      const backendConvId = resolved.isFirstMessage
        ? undefined
        : resolved.conversationId;

      const abort = sendMessageStream(
        {
          message: text,
          context,
          conversationId: backendConvId,
          allowWrites: options?.allowWrites ?? false,
          mode: options?.mode,
          conversationContext,
          systemPrompt: systemPrompt ?? NEW_STUDIO_SYSTEM_PROMPT,
        },
        {
          onInit: (newConversationId) => {
            if (newConversationId !== activeConversationId) {
              promoteDraft(
                { from: activeConversationId, to: newConversationId },
                text,
              );
              const pendingAbort =
                abortStreamsRef.current.get(activeConversationId);
              if (pendingAbort) {
                abortStreamsRef.current.delete(activeConversationId);
                abortStreamsRef.current.set(newConversationId, pendingAbort);
              }
              activeConversationId = newConversationId;
            }
          },
          onText: (chunk) => {
            accumulatedText += chunk;
            updateMessage(
              activeConversationId,
              assistantMsg.messageId,
              (m) => ({
                ...m,
                message: accumulatedText,
                status: MessageStatus.Streaming,
              }),
            );
          },
          onToolCallStart: (id, name) => {
            toolRounds++;
            updateMessage(
              activeConversationId,
              assistantMsg.messageId,
              (m) => ({
                ...m,
                toolCalls: [
                  ...(m.toolCalls ?? []),
                  { id, name, status: "executing" } satisfies ToolCallEntry,
                ],
              }),
            );
          },
          onToolResult: (id, content) => {
            updateMessage(
              activeConversationId,
              assistantMsg.messageId,
              (m) => ({
                ...m,
                toolCalls: (m.toolCalls ?? []).map((tc) =>
                  tc.id === id
                    ? { ...tc, status: "complete" as const, result: content }
                    : tc,
                ),
              }),
            );
          },
          onDone: () => {
            updateMessage(
              activeConversationId,
              assistantMsg.messageId,
              (m) => ({
                ...m,
                status: MessageStatus.Complete,
                toolRounds,
                toolCalls: (m.toolCalls ?? []).map((tc) =>
                  tc.status === "executing"
                    ? { ...tc, status: "complete" as const }
                    : tc,
                ),
              }),
            );
            setConversationLoading(activeConversationId, false);
            abortStreamsRef.current.delete(activeConversationId);
            if (toolRounds > 0) {
              setToolActivityVersion((v) => v + 1);
            }
          },
          onError: (errorMsg) => {
            updateMessage(
              activeConversationId,
              assistantMsg.messageId,
              (m) => ({
                ...m,
                message: accumulatedText || `Error: ${errorMsg}`,
                status: MessageStatus.Error,
                toolCalls: (m.toolCalls ?? []).map((tc) =>
                  tc.status === "executing"
                    ? { ...tc, status: "error" as const }
                    : tc,
                ),
              }),
            );
            setConversationLoading(activeConversationId, false);
            abortStreamsRef.current.delete(activeConversationId);
          },
        },
      );

      abortStreamsRef.current.set(resolved.conversationId, abort);
    },
    [
      inputValue,
      currentConversationId,
      messagesByConversation,
      addConversation,
      initializeConversation,
      updateTitle,
      promoteDraft,
      appendMessage,
      updateMessage,
      setConversationLoading,
      conversationContext,
    ],
  );

  const value: AiChatContextValue = {
    messages,
    messagesLoaded,
    currentConversationId,
    conversations,
    inputValue,
    queuedMessage,
    isLoading,
    isLoadingConversations,
    showAiSidebar,
    toolActivityVersion,
    setInputValue,
    queueMessage,
    clearQueuedMessage,
    setShowAiSidebar,
    sendUserMessage,
    sendUserMessageStream,
    newConversation,
    selectConversation,
    deleteConversation,
    renameConversation,
    stopGeneration,
  };

  return (
    <AiChatContext.Provider value={value}>{children}</AiChatContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

export function useAiChatContext(): AiChatContextValue {
  const context = useContext(AiChatContext);
  if (!context) {
    throw new Error("useAiChatContext must be used within AiChatProvider");
  }
  return context;
}

export default AiChatContext;
